import React, { useState, useEffect, useCallback } from 'react';
import { financialImpactApi } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #334155' };
const badge = (color) => ({ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 });

function ProvenanceBadge({ value }) {
  const colors = { FAKT: '#22c55e', SCHÄTZUNG: '#f59e0b', INDIKATOR: '#818cf8' };
  return <span style={badge(colors[value] || '#64748b')}>{value}</span>;
}

function fmt(v) { return (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function SimulationPage() {
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('baseline');

  // Scenario state
  const [scenarioActions, setScenarioActions] = useState([]);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Target saving state
  const [targetInput, setTargetInput] = useState('');
  const [targetResult, setTargetResult] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);

  useEffect(() => {
    financialImpactApi.baseline()
      .then(data => { setBaseline(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const toggleCancel = useCallback((vertragId) => {
    setScenarioActions(prev => {
      const exists = prev.find(a => a.vertragId === vertragId && a.type === 'CANCEL');
      if (exists) return prev.filter(a => !(a.vertragId === vertragId && a.type === 'CANCEL'));
      return [...prev.filter(a => a.vertragId !== vertragId), { type: 'CANCEL', vertragId }];
    });
    setScenarioResult(null);
  }, []);

  const runScenario = useCallback(async () => {
    if (scenarioActions.length === 0) return;
    setSimLoading(true);
    try {
      const result = await financialImpactApi.scenario(scenarioActions);
      setScenarioResult(result);
    } catch (err) {
      alert('Simulation fehlgeschlagen: ' + err.message);
    } finally {
      setSimLoading(false);
    }
  }, [scenarioActions]);

  const runTargetSaving = useCallback(async () => {
    const val = parseFloat(targetInput.replace(',', '.'));
    if (isNaN(val) || val < 0) { alert('Bitte einen gültigen Betrag eingeben'); return; }
    setTargetLoading(true);
    try {
      const result = await financialImpactApi.targetSaving(val);
      setTargetResult(result);
    } catch (err) {
      alert('Berechnung fehlgeschlagen: ' + err.message);
    } finally {
      setTargetLoading(false);
    }
  }, [targetInput]);

  if (loading) return <div style={{ padding: 32, color: '#94a3b8', textAlign: 'center' }}>⏳ Lade Portfolio-Daten…</div>;
  if (error) return <div style={{ padding: 32, color: '#ef4444' }}>Fehler: {error}</div>;

  const tabs = [
    { id: 'baseline', label: '📊 Portfolio' },
    { id: 'scenario', label: '🔮 Was-wäre-wenn' },
    { id: 'target', label: '🎯 Sparziel' },
  ];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, margin: 0 }}>💶 Finanzsimulator</h1>
        <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>Was bedeutet das finanziell? — Kein Vertrag wird verändert.</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#0f172a', borderRadius: 10, padding: 4 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
            background: tab === t.id ? '#1e293b' : 'transparent',
            color: tab === t.id ? '#f1f5f9' : '#64748b',
          }}>{t.label}</button>
        ))}
      </div>

      {/* BASELINE TAB */}
      {tab === 'baseline' && baseline && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Monatliche Kosten', value: fmt(baseline.monthlyCost) + ' €', color: '#818cf8' },
              { label: 'Jährliche Kosten', value: fmt(baseline.annualCost) + ' €', color: '#22d3ee' },
              { label: 'Aktive Verträge', value: baseline.contractCount, color: '#22c55e' },
              { label: 'Kosten unbekannt', value: baseline.contractsWithUnknownCost, color: '#f59e0b' },
            ].map(kpi => (
              <div key={kpi.label} style={{ ...card, padding: 16, marginBottom: 0 }}>
                <div style={{ color: kpi.color, fontSize: 20, fontWeight: 700 }}>{kpi.value}</div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{kpi.label}</div>
              </div>
            ))}
          </div>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ color: '#f1f5f9', fontWeight: 600 }}>Vertragsübersicht</span>
              <ProvenanceBadge value={baseline.provenance} />
            </div>
            {baseline.breakdown.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: 13 }}>Keine aktiven Verträge gefunden.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      {['Vertrag', 'Anbieter', 'Intervall', 'Kosten', '∅ Monatlich'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: '#64748b', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {baseline.breakdown.map(c => (
                      <tr key={c.vertragId} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '8px 8px', color: '#f1f5f9' }}>{c.titel}</td>
                        <td style={{ padding: '8px 8px', color: '#94a3b8' }}>{c.anbieter || '—'}</td>
                        <td style={{ padding: '8px 8px', color: '#94a3b8' }}>{c.zahlungsintervall}</td>
                        <td style={{ padding: '8px 8px', color: '#94a3b8' }}>{c.kosten !== null ? fmt(c.kosten) + ' €' : <span style={{ color: '#f59e0b' }}>unbekannt</span>}</td>
                        <td style={{ padding: '8px 8px', color: '#22c55e', fontWeight: 600 }}>{fmt(c.monthlyCost)} €</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #334155' }}>
                      <td colSpan={4} style={{ padding: '8px 8px', color: '#94a3b8', fontWeight: 600 }}>Gesamt</td>
                      <td style={{ padding: '8px 8px', color: '#818cf8', fontWeight: 700 }}>{fmt(baseline.monthlyCost)} €</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* SCENARIO TAB */}
      {tab === 'scenario' && baseline && (
        <>
          <div style={card}>
            <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 12 }}>
              Wähle Verträge zum Kündigen
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16 }}>
              Aktiviere Verträge und klicke „Simulieren". Kein Vertrag wird tatsächlich verändert.
            </div>
            {baseline.breakdown.length === 0 ? (
              <div style={{ color: '#64748b' }}>Keine aktiven Verträge.</div>
            ) : (
              baseline.breakdown.map(c => {
                const selected = scenarioActions.some(a => a.vertragId === c.vertragId && a.type === 'CANCEL');
                return (
                  <div key={c.vertragId} onClick={() => toggleCancel(c.vertragId)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                    background: selected ? '#1e3a5f' : '#0f172a', border: `1px solid ${selected ? '#3b82f6' : '#334155'}`,
                    transition: 'all 0.15s',
                  }}>
                    <span style={{ fontSize: 18 }}>{selected ? '✅' : '⬜'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f1f5f9', fontSize: 13 }}>{c.titel}</div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>{c.anbieter || '—'} · {c.zahlungsintervall}</div>
                    </div>
                    <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 13 }}>{fmt(c.monthlyCost)} €/mo</div>
                  </div>
                );
              })
            )}
            <button onClick={runScenario} disabled={simLoading || scenarioActions.length === 0} style={{
              marginTop: 12, padding: '10px 24px', background: scenarioActions.length > 0 ? '#818cf8' : '#334155',
              color: '#f1f5f9', border: 'none', borderRadius: 8, cursor: scenarioActions.length > 0 ? 'pointer' : 'default',
              fontWeight: 600, fontSize: 14, width: '100%',
            }}>
              {simLoading ? '⏳ Simuliere…' : `🔮 Szenario simulieren (${scenarioActions.length} ausgewählt)`}
            </button>
          </div>

          {scenarioResult && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>Simulations-Ergebnis</span>
                <ProvenanceBadge value={scenarioResult.provenance} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Bisher monatlich', value: fmt(scenarioResult.baseline.monthlyCost) + ' €', color: '#94a3b8' },
                  { label: 'Neu monatlich', value: fmt(scenarioResult.newBaseline.monthlyCost) + ' €', color: '#22c55e' },
                  { label: 'Ersparnis/Monat', value: fmt(scenarioResult.delta.savedMonthly) + ' €', color: '#818cf8' },
                ].map(kpi => (
                  <div key={kpi.label} style={{ background: '#0f172a', borderRadius: 8, padding: 12 }}>
                    <div style={{ color: kpi.color, fontSize: 18, fontWeight: 700 }}>{kpi.value}</div>
                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{kpi.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Hochrechnungen:</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[scenarioResult.projections12m, scenarioResult.projections24m, scenarioResult.projections36m].map(p => (
                  <div key={p.months} style={{ flex: 1, background: '#0f172a', borderRadius: 8, padding: 10, textAlign: 'center' }}>
                    <div style={{ color: '#22d3ee', fontSize: 16, fontWeight: 700 }}>{fmt(p.savedTotal)} €</div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>{p.months} Monate</div>
                    <div style={{ color: '#475569', fontSize: 10 }}>bis {p.timeline}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* TARGET SAVING TAB */}
      {tab === 'target' && (
        <>
          <div style={card}>
            <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 8 }}>Monatliches Sparziel</div>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16 }}>
              Wie viel möchtest du pro Monat einsparen? Der Simulator zeigt, welche Verträge du kündigen müsstest.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text" inputMode="decimal" placeholder="z.B. 30"
                value={targetInput} onChange={e => { setTargetInput(e.target.value); setTargetResult(null); }}
                style={{ flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14 }}
              />
              <span style={{ display: 'flex', alignItems: 'center', color: '#94a3b8', fontSize: 14 }}>€/Monat</span>
              <button onClick={runTargetSaving} disabled={targetLoading || !targetInput} style={{
                padding: '10px 20px', background: targetInput ? '#818cf8' : '#334155', color: '#f1f5f9',
                border: 'none', borderRadius: 8, cursor: targetInput ? 'pointer' : 'default', fontWeight: 600,
              }}>
                {targetLoading ? '⏳' : 'Berechnen'}
              </button>
            </div>
          </div>

          {targetResult && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>Sparziel-Analyse</span>
                <ProvenanceBadge value={targetResult.provenance} />
                {targetResult.canReachTarget
                  ? <span style={badge('#22c55e')}>✅ Ziel erreichbar</span>
                  : <span style={badge('#ef4444')}>⚠️ Ziel nicht erreichbar</span>
                }
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Sparziel', value: fmt(targetResult.targetMonthlySaving) + ' €' },
                  { label: 'Aktuell monatlich', value: fmt(targetResult.currentMonthlyCost) + ' €' },
                  { label: 'Max. erreichbar', value: fmt(targetResult.maxAchievableSaving) + ' €' },
                ].map(kpi => (
                  <div key={kpi.label} style={{ background: '#0f172a', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                    <div style={{ color: '#818cf8', fontSize: 16, fontWeight: 700 }}>{kpi.value}</div>
                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{kpi.label}</div>
                  </div>
                ))}
              </div>
              {targetResult.recommendations.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 13 }}>Keine Kündigungsempfehlungen für dieses Ziel.</div>
              ) : (
                <>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Empfohlene Kündigungen:</div>
                  {targetResult.recommendations.map((r, i) => (
                    <div key={r.vertragId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#0f172a', borderRadius: 8, marginBottom: 6 }}>
                      <span style={{ background: '#1e3a5f', color: '#818cf8', borderRadius: 6, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{i + 1}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#f1f5f9', fontSize: 13 }}>{r.titel}</div>
                        <div style={{ color: '#64748b', fontSize: 11 }}>{r.anbieter || '—'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 13 }}>−{fmt(r.potentialSaving)} €/mo</div>
                        <div style={{ color: '#64748b', fontSize: 11 }}>kum. {fmt(r.cumulativeSaving)} €</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
